import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { resolvePersonLabel } from '../labels';
import { INITIAL_SUN_X, INITIAL_SUN_Y } from '../constants';
import type { Person, ChartSettings } from '../types';

interface PersonLabelProps {
  person: Person;
  px: number;
  py: number;
  refX: number;
  refY: number;
  settings: ChartSettings;
}

export default function PersonLabel({ person, px, py, refX, refY, settings }: PersonLabelProps) {
  const resolved = useMemo(
    () => resolvePersonLabel(person, px, py, refX, refY, INITIAL_SUN_X, INITIAL_SUN_Y, settings),
    [person, px, py, refX, refY, settings],
  );
  const textRef = useRef<SVGGElement | null>(null);
  const [vacBox, setVacBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    if (resolved.hidden || !person.vacant || !settings.showVacBox || !textRef.current) {
      setVacBox(null);
      return undefined;
    }
    let cancelled = false;
    const measure = () => {
      if (cancelled || !textRef.current) return;
      const b = textRef.current.getBBox();
      if (!b.width && !b.height) return;
      setVacBox({ x: b.x - 7, y: b.y - 5, width: b.width + 14, height: b.height + 10 });
    };
    measure();
    const raf = requestAnimationFrame(measure);
    document.fonts?.ready?.then(measure).catch(() => {});
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [resolved.hidden, person.vacant, resolved.x, resolved.y, resolved.lines, settings.showVacBox]);

  if (resolved.hidden) return null;
  const transform = resolved.rotation
    ? `rotate(${resolved.rotation.toFixed(2)} ${resolved.x.toFixed(2)} ${resolved.y.toFixed(2)})`
    : undefined;
  return (
    <g className="lbl" transform={transform}>
      {vacBox && <rect className="vacbox" rx={7} x={vacBox.x} y={vacBox.y} width={vacBox.width} height={vacBox.height} />}
      {resolved.leaderLine && (
        <line className="leader"
          x1={resolved.leaderLine.x1} y1={resolved.leaderLine.y1}
          x2={resolved.leaderLine.x2} y2={resolved.leaderLine.y2} />
      )}
      <g className="lbltxt" ref={textRef}>
        {resolved.lines.map((line, i) => (
          <text key={i} className={line.className}
            x={resolved.x} y={resolved.y + line.dy}
            textAnchor={resolved.anchor}
            style={line.style ? parseInlineStyle(line.style) : undefined}
          >{line.text}</text>
        ))}
      </g>
    </g>
  );
}

function parseInlineStyle(cssText: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const decl of cssText.split(';')) {
    const [prop, value] = decl.split(':').map(s => s?.trim());
    if (!prop || !value) continue;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    out[camel] = value;
  }
  return out;
}
